/**
 * Session-Handling für den Apple-Web-Login.
 *
 * Der Backend-Callback leitet mit `#session=<token>` zurück. Das Token wird
 * einmal aus dem Fragment gelesen, in localStorage abgelegt und das Fragment
 * sofort aus der Adresszeile entfernt — sonst steht es im Browserverlauf und
 * wird beim Teilen des Links mitkopiert.
 */

const KEY = 'reise.session';

/** Liest ein Token aus dem URL-Fragment und räumt die Adresszeile auf. */
export function adoptTokenFromFragment(): string | null {
  const frag = location.hash.startsWith('#') ? location.hash.slice(1) : '';
  if (!frag) return null;

  const params = new URLSearchParams(frag);
  const token = params.get('session');
  const error = params.get('error');

  if (token || error) {
    // Fragment entfernen, ohne einen Verlaufseintrag zu erzeugen.
    history.replaceState(null, '', location.pathname + location.search);
  }
  if (token) {
    localStorage.setItem(KEY, token);
    return token;
  }
  if (error) throw new Error(anmeldeFehlerText(error));
  return null;
}

export function getToken(): string | null {
  return localStorage.getItem(KEY);
}

export function clearToken(): void {
  localStorage.removeItem(KEY);
}

/**
 * Übersetzt die Fehlercodes aus main.py in etwas Lesbares.
 *
 * Exportiert, weil das hier die einzige Stelle ist, an der ein neuer Code im
 * Backend still durchfallen kann: Er landet dann im Zweig ganz unten und steht
 * roh auf der Startseite. Ein Test je Code kostet eine Zeile und fängt genau
 * das ab.
 */
export function anmeldeFehlerText(code: string): string {
  switch (code) {
    case 'apple_unconfigured':
      return 'Der Apple-Login ist auf dem Server noch nicht eingerichtet (APPLE_SERVICES_ID fehlt).';
    case 'apple_state':
      return 'Der Anmeldevorgang ist abgelaufen. Bitte noch einmal versuchen.';
    case 'apple_denied':
      return 'Die Anmeldung wurde bei Apple abgebrochen.';
    case 'apple_verify':
      return 'Apples Antwort konnte nicht verifiziert werden.';
    // Die Anmeldung ist geschlossen — der Regelfall, kein Fehler. Der Satz
    // sagt, was Sache ist, und verrät nicht, ob es diese Apple-ID hier je gab.
    case 'kein_zugang':
      return 'Für diese Apple-ID ist auf diesem Server kein Zugang eingerichtet.';
    case 'gesperrt':
      return 'Dieser Zugang ist stillgelegt.';
    // Der Server prüft die Einladung, bevor er jemanden durch den Apple-Ablauf
    // schickt, und wirft ihn bei einer unbrauchbaren sofort hierher zurück.
    // Derselbe Satz wie auf der Einladungsseite selbst — es ist derselbe
    // Befund, und zwei Formulierungen für eine Sache lesen sich wie zwei Fälle.
    case 'einladung_ungueltig':
      return 'Diese Einladung gilt nicht mehr — abgelaufen, zurückgenommen oder schon benutzt. Frag die Person, die dich eingeladen hat, nach einer neuen.';
    default:
      return `Anmeldung fehlgeschlagen (${code}).`;
  }
}
